import type { ChangeEvent } from 'react'
import React, { useCallback, useMemo, useState, useEffect } from 'react'
import { useIntl } from 'react-intl'
import TextArea from 'components/Common/Form/Input/TextArea'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LineBurgerIcon from 'LuxKit/Icons/line/LineBurgerIcon'
import LineDiningCreditIcon from 'LuxKit/Icons/line/LineDiningCreditIcon'
import LineFestivalsIcon from 'LuxKit/Icons/line/LineFestivalsIcon'
import LineToasterIcon from 'LuxKit/Icons/line/LineToasterIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import { arrayToMap } from 'lib/array/arrayUtils'
import TileCard from 'tripPlanner/components/EntryPoints/Common/Tile/TileCard'
import DietaryRequirements from './DietryRequirements'
import * as Analytics from 'analytics/analytics'

interface Props {
  hotelCity: string
  foodStyles: Array<{
    foodStyle: string
    selected: boolean
  }>
  dietaryRequirements: Array<{
    dietaryRequirement: string
    selected: boolean
  }>
  onFoodStyleSelect: (foodStyle: string) => void
  onDietaryRequirementSelect: (dietaryRequirement: string) => void
  isGeneratingTrip: boolean
  stepNumber: number
  totalSteps: number
}

function AITripFoodStyleSelection({
  hotelCity,
  foodStyles,
  dietaryRequirements,
  onFoodStyleSelect,
  onDietaryRequirementSelect,
  isGeneratingTrip,
  stepNumber,
  totalSteps,
}: Props) {
  const intl = useIntl()
  const [customDietaryRequirement, setCustomDietaryRequirement] = useState('')

  const foodStyleMap = useMemo(() =>
    arrayToMap(foodStyles, style => style.foodStyle, style => style.selected),
  [foodStyles])

  const handleDietaryRequirementSelect = useCallback((dietaryRequirement: string) => {
    onDietaryRequirementSelect(dietaryRequirement)
  }, [onDietaryRequirementSelect])

  const handleCustomDietaryRequirementChange = useCallback((e: ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value
    setCustomDietaryRequirement(newValue)
    if (newValue.trim()) {
      onDietaryRequirementSelect(`Custom: ${newValue.trim()}`)
    } else {
      onDietaryRequirementSelect('Custom:') // Clear when it's empty
    }
  }, [onDietaryRequirementSelect])

  useEffect(() => {
    Analytics.trackClientEvent({
      subject: 'food_page',
      action: 'view',
      category: 'trip_planner',
      type: 'nonInteraction',
    })
  }, [])

  const FOOD_STYLES = [
    {
      foodStyle: 'Keep It Simple',
      description: intl.formatMessage({
        id: 'fj7BEhjaJu0w3FeioCVp',
        defaultMessage: 'Food’s not my focus.',
        description: 'Description for simple food style option in the trip planner setup wizard',
      }),
      icon: <LineToasterIcon />,
    },
    {
      foodStyle: 'Casual Indulgence',
      description: intl.formatMessage({
        id: 'BMWJEGAnm6o1wD4eq7WJ',
        defaultMessage: 'Enjoy food, but not chasing it.',
        description: 'Description for casual food style option in the trip planner setup wizard',
      }),
      icon: <LineBurgerIcon />,
    },
    {
      foodStyle: 'Only the Best',
      description: intl.formatMessage({
        id: 'LWqsu_KDm1rDWYzWhB3K',
        defaultMessage: 'I’m here for quality.',
        description: 'Description for quality-focused food style option on the form',
      }),
      icon: <LineDiningCreditIcon />,
    },
    {
      foodStyle: 'Full-on Foodie',
      description: intl.formatMessage({
        id: '8-RggdlNyPBFjtH0C_GI',
        defaultMessage: 'Every meal is an experience.',
        description: 'Description for foodie food style option for the experience',
      }),
      icon: <LineFestivalsIcon />,
    },
  ]

  const stepText = intl.formatMessage(
    {
      id: 'ewX_UMLdx4cN8zd8qwrw',
      defaultMessage: '{stepNumber} of {totalSteps}',
      description: 'Step indicator showing current step number and total steps',
    },
    { stepNumber, totalSteps },
  )

  const headingText = intl.formatMessage(
    {
      id: '3fBDCCiz0dPhK91uHpqt',
      defaultMessage: 'Now, let’s talk about <i>food</i>',
      description: 'Heading text with italicized food in the trip planner setup wizard',
    },
    {
      i: (chunks: React.ReactNode) => <i key="italic">{chunks}</i>,
    },
  )

  const descriptionText = intl.formatMessage(
    {
      id: 'j75oxYaX3EjDsRK1ww3E',
      defaultMessage: "How would you like to experience {hotelCity}'s culinary scene?",
      description: 'Question asking about food preferences on the hotel or room booking page',
    },
    { hotelCity },
  )

  const dietaryRequirementsLabel = intl.formatMessage(
    {
      id: 'KS3W5kf-VH6Zn2skcOR0',
      defaultMessage: '<strong>Dietary requirements</strong> (optional)',
      description: 'Label for dietary requirements section with optional indicator',
    },
    {
      strong: (chunks: React.ReactNode) => <strong key="bold">{chunks}</strong>,
    },
  )

  const otherDietaryRequirementsLabel = intl.formatMessage(
    {
      id: 'cyiZuylWrNecl-812PE1',
      defaultMessage: '<strong>Any other dietary requirements?</strong> (optional)',
      description: 'Label for custom dietary requirements input with optional indicator',
    },
    {
      strong: (chunks: React.ReactNode) => <strong key="bold">{chunks}</strong>,
    },
  )

  const customDietaryPlaceholder = intl.formatMessage({
    id: 'wa57nFnWSpdlMaag8_y9',
    defaultMessage: 'e.g., Kosher, dairy-free, nut allergies, keto, low sodium...',
    description: 'Placeholder text for custom dietary requirements input',
  })

  return (
    <Group direction="vertical" gap={20} desktopGap={24}>
      <VerticalSpacer gap={8}>
        <BodyText variant="large" weight="bold">{stepText}</BodyText>
        <Heading variant="heading1">
          {headingText}
        </Heading>
        <BodyText variant="large">
          {descriptionText}
        </BodyText>
      </VerticalSpacer>

      <Group direction="vertical" gap={16} desktopGap={20}>
        {FOOD_STYLES.map(style => (
          <TileCard
            key={style.foodStyle}
            title={style.foodStyle}
            subtitle={style.description}
            icon={style.icon}
            value={style.foodStyle}
            layout="horizontal"
            onClick={onFoodStyleSelect}
            selected={!!foodStyleMap.get(style.foodStyle)}
            disabled={isGeneratingTrip}
          />))}
      </Group>

      <VerticalSpacer gap={12}>
        <BodyText variant="large">
          {dietaryRequirementsLabel}
        </BodyText>
        <DietaryRequirements
          dietaryRequirements={dietaryRequirements}
          onDietaryRequirementSelect={handleDietaryRequirementSelect}
        />
      </VerticalSpacer>

      <VerticalSpacer gap={12}>
        <BodyText variant="large">
          {otherDietaryRequirementsLabel}
        </BodyText>
        <TextArea
          placeholder={customDietaryPlaceholder}
          value={customDietaryRequirement}
          onChange={handleCustomDietaryRequirementChange}
          rows={3}
        />
      </VerticalSpacer>
    </Group>
  )
}

export default AITripFoodStyleSelection
